/**
 * Desfecho do atendimento — substitui o antigo painel "Retorno".
 *
 * O cenário é DETECTADO do que já foi preenchido na evolução (indicações e
 * pedidos de exame), e só as perguntas que faltam aparecem. O médico vê de onde
 * veio a detecção e para qual funil o paciente vai, e pode trocar o cenário se
 * discordar. Quando não é possível classificar, não adivinhamos: pedimos a
 * escolha e explicamos como automatizar.
 */
import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import DurationField from './DurationField';
import {
  describeCrmPlan,
  detectOutcomeScenario,
  needsPreOpExamQuestion,
  resolveCrmPlan,
  resolveEffectiveOutcome,
  validateOutcome,
  type ClinicalDisposition,
  type EvolutionOutcome,
  type ExamFollowUp,
  type OutcomeScenario,
  type SurgeryPreOpReturn,
} from './evolutionOutcomeModel';
import {
  evolutionSectionHeaderBarSxFor,
  evolutionSectionPaperSxFor,
  evolutionSectionBodySx,
  evolutionSectionTitleSx,
  EVOLUTION_THEME,
} from './evolutionTheme';
import type { EvolutionAttachedPrintable, EvolutionIndicationItem } from './evolutionWorkflowTypes';
import { useClinicMedicalSpecialty } from '../../hooks/useClinicMedicalSpecialty';

interface EvolutionOutcomeSectionProps {
  outcome: EvolutionOutcome;
  onChange: (next: EvolutionOutcome) => void;
  indicationItems: EvolutionIndicationItem[];
  printables: EvolutionAttachedPrintable[];
  /** Mostra as pendências em vermelho — ligado após uma tentativa de concluir. */
  showValidation?: boolean;
  /** Evolução assinada abre em leitura. */
  disabled?: boolean;
  compact?: boolean;
}

const SCENARIO_LABEL: Record<Exclude<OutcomeScenario, 'unclassified'>, string> = {
  surgery: 'Cirurgia / procedimento',
  exam: 'Exame',
  clinical: 'Só clínico',
};

const toggleGroupSx = {
  flexWrap: 'wrap' as const,
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: `1px solid ${EVOLUTION_THEME.border}`,
    borderRadius: '999px !important',
    px: 1.75,
    py: 0.6,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.82rem',
    color: EVOLUTION_THEME.slateMid,
    '&.Mui-selected': {
      bgcolor: EVOLUTION_THEME.teal,
      color: '#fff',
      borderColor: EVOLUTION_THEME.teal,
      '&:hover': { bgcolor: EVOLUTION_THEME.tealDark },
    },
  },
};

const questionLabelSx = {
  display: 'block',
  fontWeight: 700,
  color: EVOLUTION_THEME.navy,
  mb: 0.75,
};

const EvolutionOutcomeSection: React.FC<EvolutionOutcomeSectionProps> = ({
  outcome,
  onChange,
  indicationItems,
  printables,
  showValidation = false,
  disabled = false,
  compact = false,
}) => {
  const { isFisioterapia } = useClinicMedicalSpecialty();

  const rawDetection = useMemo(
    () => detectOutcomeScenario({ indicationItems, printables }),
    [indicationItems, printables],
  );

  const detection = useMemo(() => {
    if (!isFisioterapia) return rawDetection;
    if (rawDetection.scenario === 'surgery' || rawDetection.scenario === 'exam') {
      return { ...rawDetection, scenario: 'clinical' as OutcomeScenario };
    }
    return rawDetection;
  }, [isFisioterapia, rawDetection]);

  const scenarioToggleKeys = useMemo(() => {
    const all = Object.keys(SCENARIO_LABEL) as Array<keyof typeof SCENARIO_LABEL>;
    return isFisioterapia ? all.filter((key) => key === 'clinical') : all;
  }, [isFisioterapia]);

  // O cenário detectado vale até o médico escolher outro explicitamente.
  const scenario: OutcomeScenario =
    outcome.scenarioSource === 'manual' && outcome.scenario ? outcome.scenario : detection.scenario;

  const effectiveOutcome = useMemo<EvolutionOutcome>(
    () => resolveEffectiveOutcome(outcome, detection),
    [outcome, detection],
  );

  const pending = validateOutcome(effectiveOutcome, detection);
  const plans = pending ? [] : resolveCrmPlan(effectiveOutcome, detection);

  const patch = (next: Partial<EvolutionOutcome>) => onChange({ ...outcome, ...next });
  const patchSurgery = (next: Partial<EvolutionOutcome['surgery']>) =>
    onChange({ ...outcome, surgery: { ...outcome.surgery, ...next } });
  const patchExam = (next: Partial<EvolutionOutcome['exam']>) =>
    onChange({ ...outcome, exam: { ...outcome.exam, ...next } });
  const patchClinical = (next: Partial<EvolutionOutcome['clinical']>) =>
    onChange({ ...outcome, clinical: { ...outcome.clinical, ...next } });

  const chooseScenario = (next: OutcomeScenario) =>
    patch({ scenario: next, scenarioSource: 'manual' });

  // Atendimento só clínico: padrão "Sem reconvocação" (evoluções antigas ou ao voltar de outro cenário).
  useEffect(() => {
    if (disabled) return;
    if (scenario !== 'clinical') return;
    if (outcome.clinical.disposition) return;
    patchClinical({ disposition: 'no_recall' });
  }, [scenario, outcome.clinical.disposition, disabled]);

  useEffect(() => {
    if (!isFisioterapia || disabled) return;
    if (outcome.scenario === 'surgery' || outcome.scenario === 'exam') {
      patch({ scenario: 'clinical', scenarioSource: 'manual' });
    }
  }, [isFisioterapia, disabled, outcome.scenario]);

  const detectionSummary = () => {
    if (detection.scenario === 'surgery') {
      return {
        title: 'Indicou cirurgia / procedimento',
        detail: detection.surgeryItems.map((i) => i.name).join(' · '),
        origin: 'das indicações',
      };
    }
    if (detection.scenario === 'exam') {
      const names = detection.examOrderNames.length
        ? detection.examOrderNames
        : detection.examItems.map((i) => i.name);
      return {
        title: 'Pediu exame',
        detail: names.join(' · '),
        origin: detection.hasExamOrder ? 'do pedido de exames' : 'das indicações',
      };
    }
    if (detection.scenario === 'clinical') {
      return {
        title: 'Atendimento clínico',
        detail: 'Nenhuma cirurgia ou exame indicado nesta consulta',
        origin: null,
      };
    }
    return null;
  };

  const summary = detectionSummary();
  const overridden = outcome.scenarioSource === 'manual' && detection.scenario !== scenario;

  const bodyPad = evolutionSectionBodySx(compact);
  const blockGap = compact ? 1 : 2;

  return (
    <Paper elevation={0} sx={evolutionSectionPaperSxFor(compact)}>
      <Box sx={evolutionSectionHeaderBarSxFor(compact)}>
        <Typography variant="overline" sx={{ ...evolutionSectionTitleSx, lineHeight: compact ? 1.2 : undefined }}>
          Desfecho
        </Typography>
      </Box>

      <Box sx={bodyPad}>
        {detection.scenario === 'unclassified' ? (
          <Alert severity="warning" sx={{ mb: blockGap, py: compact ? 0 : undefined, borderRadius: compact ? 1.5 : 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Não foi possível classificar {detection.unclassifiedItems.map((i) => i.name).join(', ')}.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Escolha o desfecho abaixo. Para que o sistema acerte sozinho na próxima vez, defina a
              natureza do ato clínico na categoria desse procedimento em Configurações &gt; Procedimentos.
            </Typography>
          </Alert>
        ) : (
          summary && (
            <Box
              sx={{
                mb: blockGap,
                p: compact ? 1 : 1.5,
                borderRadius: compact ? 1.5 : 2,
                bgcolor: EVOLUTION_THEME.tealSoft,
                border: `1px solid ${EVOLUTION_THEME.border}`,
                // Faixa de acento à esquerda: sinaliza "isto foi deduzido, não digitado".
                borderLeft: `3px solid ${EVOLUTION_THEME.teal}`,
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: EVOLUTION_THEME.tealDark }}>
                  {summary.title}
                </Typography>
                {summary.origin && (
                  <Chip
                    label={summary.origin}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(255,255,255,0.75)',
                      color: EVOLUTION_THEME.slateMid,
                    }}
                  />
                )}
              </Stack>
              {summary.detail && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: EVOLUTION_THEME.slateMid }}>
                  {summary.detail}
                </Typography>
              )}
            </Box>
          )
        )}

        <Box sx={{ mb: blockGap }}>
          <Typography variant="caption" sx={{ ...questionLabelSx, mb: compact ? 0.35 : 0.75 }}>
            {detection.scenario === 'unclassified' ? 'Desfecho deste atendimento *' : 'Desfecho'}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            disabled={disabled}
            value={scenario === 'unclassified' ? null : scenario}
            onChange={(_, v) => v && chooseScenario(v as OutcomeScenario)}
            sx={toggleGroupSx}
          >
            {scenarioToggleKeys.map((key) => (
              <ToggleButton key={key} value={key}>
                {SCENARIO_LABEL[key]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {overridden && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: EVOLUTION_THEME.slate }}>
              Desfecho alterado manualmente — o detectado era {SCENARIO_LABEL[detection.scenario as keyof typeof SCENARIO_LABEL]}.
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: blockGap }} />

        {scenario === 'surgery' && (
          <Stack spacing={blockGap}>
            <Box>
              <Typography variant="caption" sx={questionLabelSx}>
                Retorno antes da cirurgia? *
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                disabled={disabled}
                value={outcome.surgery.preOpReturn}
                onChange={(_, v) => v && patchSurgery({ preOpReturn: v as SurgeryPreOpReturn })}
                sx={toggleGroupSx}
              >
                <ToggleButton value="none">Não, vai direto para a cirurgia</ToggleButton>
                <ToggleButton value="scheduled">Sim, com retorno antes</ToggleButton>
              </ToggleButtonGroup>
              {showValidation && !outcome.surgery.preOpReturn && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: EVOLUTION_THEME.cancelledText }}>
                  Escolha uma das opções.
                </Typography>
              )}
            </Box>

            {outcome.surgery.preOpReturn === 'scheduled' && (
              <>
                <DurationField
                  label="Retorno antes da cirurgia"
                  disabled={disabled}
                  required
                  showError={showValidation}
                  value={outcome.surgery.preOpReturnIn}
                  onChange={(preOpReturnIn) => patchSurgery({ preOpReturnIn })}
                />
                <TextField
                  fullWidth
                  size="small"
                  disabled={disabled}
                  label="Motivo do retorno"
                  placeholder="Ex.: reavaliar pressão intraocular antes de operar"
                  value={outcome.surgery.preOpReturnReason}
                  onChange={(e) => patchSurgery({ preOpReturnReason: e.target.value })}
                />
              </>
            )}

            {needsPreOpExamQuestion(effectiveOutcome, detection) && (
              <Box>
                <Typography variant="caption" sx={questionLabelSx}>
                  Os exames solicitados são pré-operatórios? *
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={
                    outcome.surgery.examsArePreOp === null
                      ? null
                      : outcome.surgery.examsArePreOp
                        ? 'yes'
                        : 'no'
                  }
                  onChange={(_, v) => v && patchSurgery({ examsArePreOp: v === 'yes' })}
                  sx={toggleGroupSx}
                >
                  <ToggleButton value="yes">Sim, são da cirurgia</ToggleButton>
                  <ToggleButton value="no">Não, são diagnósticos</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: EVOLUTION_THEME.slate }}>
                  Pré-operatórios entram como checklist do card de cirurgia. Diagnósticos abrem um card
                  próprio em Exames.
                </Typography>
                {showValidation && outcome.surgery.examsArePreOp === null && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: EVOLUTION_THEME.cancelledText }}>
                    Escolha uma das opções.
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  disabled={disabled}
                  checked={outcome.surgery.timePriority}
                  onChange={(e) => patchSurgery({ timePriority: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: EVOLUTION_THEME.teal },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: EVOLUTION_THEME.teal,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Prioridade de tempo
                  </Typography>
                  <Typography variant="caption" sx={{ color: EVOLUTION_THEME.slate }}>
                    A secretaria é acionada hoje, não no prazo padrão.
                  </Typography>
                </Box>
              }
            />

            <DurationField
              label="Reconvocar se não operar"
              disabled={disabled}
              value={outcome.surgery.recallIn}
              onChange={(recallIn) => patchSurgery({ recallIn })}
              helperText="Opcional. Cria um recall em paralelo, para o paciente não se perder caso desista."
            />
          </Stack>
        )}

        {scenario === 'exam' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={questionLabelSx}>
                Depois do exame? *
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                disabled={disabled}
                value={outcome.exam.followUp}
                onChange={(_, v) => v && patchExam({ followUp: v as ExamFollowUp })}
                sx={toggleGroupSx}
              >
                <ToggleButton value="back_to_doctor">Volta ao médico</ToggleButton>
                <ToggleButton value="file_only">Faz o Exame no local e arquiva</ToggleButton>
                <ToggleButton value="external">Exame externo</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: EVOLUTION_THEME.slate }}>
                Exame sem destino definido é paciente perdido, por isso este campo é obrigatório.
              </Typography>
              {showValidation && !outcome.exam.followUp && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: EVOLUTION_THEME.cancelledText }}>
                  Escolha uma das opções.
                </Typography>
              )}
            </Box>

            {outcome.exam.followUp === 'back_to_doctor' && (
              <DurationField
                label="Retorno com o resultado"
                disabled={disabled}
                required
                showError={showValidation}
                value={outcome.exam.returnIn}
                onChange={(returnIn) => patchExam({ returnIn })}
              />
            )}
          </Stack>
        )}

        {scenario === 'clinical' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={questionLabelSx}>
                Desfecho clínico *
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                disabled={disabled}
                value={outcome.clinical.disposition}
                onChange={(_, v) => v && patchClinical({ disposition: v as ClinicalDisposition })}
                sx={toggleGroupSx}
              >
                <ToggleButton value="return">Retorno assistencial</ToggleButton>
                <ToggleButton value="recall">Recall preventivo</ToggleButton>
                <ToggleButton value="referral">Encaminhado</ToggleButton>
                <ToggleButton value="no_recall">Sem reconvocação</ToggleButton>
              </ToggleButtonGroup>
              {showValidation && !outcome.clinical.disposition && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: EVOLUTION_THEME.cancelledText }}>
                  Escolha uma das opções.
                </Typography>
              )}
            </Box>

            {(outcome.clinical.disposition === 'return' || outcome.clinical.disposition === 'recall') && (
              <DurationField
                label={outcome.clinical.disposition === 'return' ? 'Retorno em' : 'Reconvocar em'}
                disabled={disabled}
                required
                showError={showValidation}
                value={outcome.clinical.returnIn}
                onChange={(returnIn) => patchClinical({ returnIn })}
              />
            )}

            {outcome.clinical.disposition === 'referral' && (
              <TextField
                fullWidth
                size="small"
                required
                disabled={disabled}
                error={showValidation && !outcome.clinical.referralTo.trim()}
                label="Encaminhado para"
                placeholder="Ex.: Dr. Ana Paula — retina"
                value={outcome.clinical.referralTo}
                onChange={(e) => patchClinical({ referralTo: e.target.value })}
                helperText="Profissional ou especialidade que vai receber o paciente."
              />
            )}
          </Stack>
        )}

        <TextField
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          disabled={disabled}
          label="Observação para o CRM"
          placeholder="Ex.: paciente prefere ser contatado à tarde"
          value={outcome.crmNote}
          onChange={(e) => patch({ crmNote: e.target.value })}
          helperText="Vai para a secretaria junto do card. Não é dado clínico."
        />

        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: `1px dashed ${EVOLUTION_THEME.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <ArrowForwardIcon
            sx={{ fontSize: 18, color: pending ? EVOLUTION_THEME.slateFaint : EVOLUTION_THEME.teal }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: pending ? EVOLUTION_THEME.slateFaint : EVOLUTION_THEME.tealDark,
            }}
          >
            {pending && showValidation ? pending : pending ? 'Responda acima para ver o destino' : describeCrmPlan(plans)}
          </Typography>
          {!pending && plans.length > 0 && (
            <Typography variant="caption" sx={{ color: EVOLUTION_THEME.slate }}>
              — card criado ao concluir, com você como responsável
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EvolutionOutcomeSection;
